import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { Search, Users, ChevronLeft, ChevronRight } from 'lucide-react'

import api from '../../../const/axios'
import { loadingOff, loadingOn } from '../../../redux/actions'
import Author from './Author'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { usePageParam } from '@/hooks/usePageParam'

const PAGE_SIZE = 20

const Authors: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Page lives in the URL (?page=N) so it survives back/forward navigation
  const { page, setPage, goToPage } = usePageParam()

  const [authors, setAuthors] = useState<any[]>([])
  const [count, setCount] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(false)
  // Track what keyword was last searched (so paging keeps the same query)
  const [committed, setCommitted] = useState('')

  const fetchAuthors = useCallback(async (pg: number, word: string) => {
    setLoading(true)
    dispatch(loadingOn() as any)
    try {
      const res = await api.get(`authors?page=${pg}&perpage=${PAGE_SIZE}&detailed=true&search=${word}`)
      setAuthors(res.data.authors)
      setCount(res.data.count)
    } finally {
      setLoading(false)
      dispatch(loadingOff() as any)
    }
  }, [dispatch])

  useEffect(() => {
    fetchAuthors(page, committed)
  }, [page, committed, fetchAuthors])

  const handleSearch = () => {
    setCommitted(keyword)
    setPage(1)
  }

  const totalPages = Math.ceil(count / PAGE_SIZE)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>3sual.az | {t('authors.title')}</title>
        <meta name="description" content="Sual ensiklopediyasının müəllifləri — müəlliflərin sualları, mövzuları və istinad mənbələri." />
        <link rel="canonical" href="https://3sual.az/authors" />
      </Helmet>

      {/* ── Page header ───────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 max-w-xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <Input
              className="pl-9 h-10 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus-visible:ring-blue-400 focus-visible:ring-offset-0"
              placeholder={t('authors.searchAuthor')}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button
            onClick={handleSearch}
            className="h-10 px-5 !rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            <Search className="w-4 h-4" />
            {t('authors.search')}
          </Button>
        </div>
      </div>

      {/* ── Grid ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {authors.length > 0 ? (
          <>
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-5">
              <span className="font-semibold text-gray-700 dark:text-gray-200">{count}</span> müəllif tapıldı
            </p>

            <div className={cn(
              'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
              loading && 'opacity-60 pointer-events-none transition-opacity'
            )}>
              {authors.map(author => (
                <Author key={author.id} author={author} />
              ))}
            </div>

            {/* ── Pagination ──────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                  .reduce<(number | 'e')[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('e')
                    acc.push(p)
                    return acc
                  }, [])
                  .map((p, i) =>
                    p === 'e' ? (
                      <span key={`e${i}`} className="w-9 text-center text-gray-400 dark:text-gray-500 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p as number)}
                        className={cn(
                          'w-9 h-9 rounded-lg text-sm font-medium border transition-colors',
                          page === p
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-900 hover:text-blue-500 dark:hover:text-indigo-200'
                        )}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:border-blue-400 dark:hover:border-blue-900 hover:text-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:hover:text-indigo-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Users className="w-14 h-14 text-gray-200 mb-4" />
            <p className="text-gray-400 dark:text-gray-500 font-medium text-lg">{t('authors.noResultsFound')}</p>
            <p className="text-gray-300 text-sm mt-1">Başqa açar söz ilə axtarın</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Authors
